/* globals $ */

import React, { Component } from "react";
import PropTypes from "prop-types";
import CSSTransitionGroup from "react-transition-group/CSSTransitionGroup";
import classnames from "classnames";
import isApp from "shared/util/isApp";
import Cookies from "shared/util/Cookies";
import {
	FACEBOOK_SIGNUP_STATES,
	PROMPTS,
	FIELDS,
	SUBFIELDS,
	DESKTOP_PROMPTS,
	FIELD_TRANSITION_SPEED,
} from "apps/signup/util/constants";

import Api from "apps/signup/util/web";
import DesktopLoggedoutHeader from "shared/components/DesktopLoggedoutHeader";
import Header from "./Header/";
import EmailOrFB from "./EmailOrFb/";
import Registration from "./Registration/";
import Button from "./Button";

import "./App.scss";

if (process.env.PLATFORM === "desktop") {
	require("./App.desktop.scss");
} else {
	require("./App.mobile.scss");
}

class App extends Component {
	static propTypes = {
		currentField: PropTypes.string,
		email: PropTypes.string.isRequired,
		captcha: PropTypes.string.isRequired,
		password: PropTypes.string.isRequired,
		updateField: PropTypes.func.isRequired,
		validate: PropTypes.func.isRequired,
		storeCaptcha: PropTypes.func.isRequired,
		setCurrentField: PropTypes.func.isRequired,
		dismissSuggestion: PropTypes.func.isRequired,
		submitRegistration: PropTypes.func.isRequired,
		facebookSignup: PropTypes.func.isRequired,
		facebookState: PropTypes.string.isRequired,
		facebookToken: PropTypes.string.isRequired,
		submitFacebookSignup: PropTypes.func.isRequired,
		feedback: PropTypes.object.isRequired,
		errors: PropTypes.object.isRequired,
		success: PropTypes.object.isRequired,
		suggestions: PropTypes.object.isRequired,
		isValidating: PropTypes.bool,
		isUpdating: PropTypes.bool,
		isSubmitting: PropTypes.bool,
		isSuccess: PropTypes.bool,
		showCaptcha: PropTypes.bool,
		confirmRecaptchaToken: PropTypes.func.isRequired,
		entryApp: PropTypes.string,
		setCaptchaV3Token: PropTypes.func.isRequired,
	};

	componentDidMount() {
		this.props.setCurrentField(this.props.currentField);

		/**
		  * This sets a cookie on the browser via javascript. This means if we see
		  * a login that doesn't contain this cookie chances are the login was
		  * automated. This is negated by tools that instrument a real browser or
		  * people who catch onto the cookie. However, this still addresses the
		  * low hanging fruit of automated logins.
		  *
		  * This snippet was originally for logins only but is now on this page
		  * too because some users sign in through facebook on the sign up page.
		  */
		Cookies.setCookie("ua", "531227642bc86f3b5fd7103a0c0b4fd6");

		// record when a user starts the funnel
		Api.recordStartOnboarding();

		if (isApp()) {
			$(this.container).immobilize(true, true);
		}
	}

	componentDidUpdate(prevProps) {
		// if we get an access token passed down, then we have already signed up
		// with facebook, so we just need to tell the api.
		if (!prevProps.facebookToken && this.props.facebookToken) {
			this.props.submitFacebookSignup();
		}
	}

	_getRenderElement = () => {
		if (!this.props.currentField) {
			return null;
		}

		switch (this.props.currentField) {
			case FIELDS.EMAIL_OR_FB: {
				return (
					<EmailOrFB
						email={this.props.email}
						onChange={this.props.updateField}
						validate={this.props.validate}
						errors={this.props.errors}
						success={this.props.success}
						suggestions={this.props.suggestions}
						feedback={this.props.feedback}
						facebookState={this.props.facebookState}
						facebookSignup={this.props.facebookSignup}
						dismissSuggestion={this.props.dismissSuggestion}
						isUpdating={this.props.isUpdating}
					/>
				);
			}

			case FIELDS.REGISTRATION: {
				return (
					<Registration
						password={this.props.password}
						onChange={this.props.updateField}
						validate={this.props.validate}
						errors={this.props.errors}
						success={this.props.success}
						suggestions={this.props.suggestions}
						feedback={this.props.feedback}
						dismissSuggestion={this.props.dismissSuggestion}
						captchaValue={this.props.captcha}
						storeCaptcha={this.props.storeCaptcha}
						showCaptcha={this.props.showCaptcha}
						confirmRecaptchaToken={this.props.confirmRecaptchaToken}
						setCaptchaV3Token={this.props.setCaptchaV3Token}
					/>
				);
			}

			default:
				return null;
		}
	}

	_fieldIsValid = () => {
		const {
			errors,
			currentField,
			isValidating,
			isUpdating,
			showCaptcha,
			captcha,
			success,
			isSubmitting,
		} = this.props;

		if (
			isUpdating || isValidating || isSubmitting ||
			errors[currentField] ||
			(showCaptcha && currentField === FIELDS.REGISTRATION && !captcha)
		) {
			return false;
		}

		if (currentField === FIELDS.EMAIL_OR_FB) {
			return success.email || success.facebook;
		} else if (SUBFIELDS[currentField]) {
			return (SUBFIELDS[currentField]).every((subfield) => {
				return success[subfield];
			});
		} else {
			return !(!this.props[currentField] || errors[currentField]);
		}
	}

	_showRegistration = () => {
		this.props.setCurrentField(FIELDS.REGISTRATION);
	}

	_getSubmitFunc = () => {
		if (this.props.currentField === FIELDS.EMAIL_OR_FB) {
			// this will be either submit facebook or email
			if (this.props.facebookToken) {
				return this.props.submitFacebookSignup;
			} else {
				return this._showRegistration;
			}
		} else if (this.props.currentField === FIELDS.REGISTRATION) {
			return this.props.submitRegistration;
		}

		return null;
	}

	_handleFormSubmit = (e) => {
		if (e && e.preventDefault) {
			e.preventDefault();
		}

		// bail if we are already trying to connect with facebook
		if (this.props.facebookState === FACEBOOK_SIGNUP_STATES.connecting) {
			return;
		}

		// bail if we are updating (the user is typing), validating, or have errors
		if (
			this.props.isUpdating ||
			this.props.isValidating ||
			this.props.errors[FIELDS.EMAIL] ||
			this.props.errors[FIELDS.PASSWORD]
		) {
			return;
		}

		const submitFunction = this._getSubmitFunc();
		submitFunction();
	}

	render() {
		const isDesktop = process.env.PLATFORM === "desktop";
		const promptList = isDesktop ? DESKTOP_PROMPTS : PROMPTS;

		const {
			currentField,
			setCurrentField,
			errors,
			feedback,
			entryApp,
			isSubmitting,
			isSuccess,
		} = this.props;

		if (!currentField) {
			return null;
		}

		const formClass = `signup2017 oknf oknf--gray signup2017--${currentField}`;

		const containerClass = classnames("signup2017-container", {
			[`signup2017-container--${entryApp}`]: !!entryApp,
		});

		const promptClass = `signup2017-prompt signup2017-prompt--${currentField}`;

		const hasHeader = currentField === FIELDS.EMAIL_OR_FB ||
			currentField === FIELDS.REGISTRATION;

		const transitionName = currentField === FIELDS.REGISTRATION ?
			"signup2017-field-fieldTransition--next" :
			"signup2017-field-fieldTransition--previous";

		// the form requires an action for apps; it turns the keyboard "return"
		// button into a "go" button
		return (
			<form
				className={formClass}
				ref={(el) => { this.container = el; }}
				onSubmit={this._handleFormSubmit}
				action="/signup"
			>
				{isDesktop && <DesktopLoggedoutHeader />}

				<div className={containerClass}>
					{hasHeader &&
						<Header
							currentField={currentField}
							setCurrentField={setCurrentField}
						/>
					}

					<CSSTransitionGroup
						transitionName="signup2017-prompt-transition"
						transitionEnterTimeout={FIELD_TRANSITION_SPEED}
						transitionLeaveTimeout={FIELD_TRANSITION_SPEED}
					>
						{promptList[currentField] &&
							<div key={currentField} className={promptClass}>
								{promptList[currentField]}
							</div>
						}
					</CSSTransitionGroup>

					<CSSTransitionGroup
						transitionName={transitionName}
						transitionEnterTimeout={FIELD_TRANSITION_SPEED}
						transitionLeaveTimeout={FIELD_TRANSITION_SPEED}
					>
						<div className="signup2017-field" key={currentField}>
							{this._getRenderElement()}
						</div>
					</CSSTransitionGroup>

					{errors[currentField] && feedback[currentField] &&
						<div className="signup2017-error">
							{feedback[currentField]}
						</div>
					}
					<Button
						currentField={currentField}
						fieldIsValid={this._fieldIsValid()}
						isSubmitting={isSubmitting}
						isSuccess={isSuccess}
						onSave={this._handleFormSubmit}
					/>
				</div>
			</form>
		);
	}
}

export default App;



// WEBPACK FOOTER //
// ./src/apps/signup/components/App.jsx